import { type FC, type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Dot, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

const STAR_RATIO = 18 / 24; // 18px star / 24px dot/button

/**
 * A star rating component with 5 icons. Dots indicate empty, stars indicate selected.
 * Click on a star to set the rating. Clicking the current rating star clears it.
 * Number keys 1-5 set or clear the rating via keyboard.
 */
export const Rating: FC<{
  rating?: number;
  onChange: (value: number) => void;
  readOnly?: boolean;
  className?: string;
  isLoading?: boolean;
  dataCy?: string;
  size?: number;
}> = ({ rating: initialRating = 0, onChange, readOnly, className, isLoading, dataCy, size = 24 }) => {
  const tCommon = useTranslations('common');
  const [rating, setRating] = useState<number>(initialRating);
  const [hoverRating, setHoverRating] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setRating(initialRating);
  }, [initialRating]);

  const displayRating = hoverRating ?? rating;

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (readOnly) {
      return;
    }

    if (/^[1-5]$/.test(e.key)) {
      const value = parseInt(e.key, 10);
      setRating(value);
      onChange(rating === value ? 0 : value);
    }
  };

  useEffect(() => {
    if (!readOnly && !isLoading) {
      containerRef.current?.focus();
    }
  }, [readOnly, isLoading]);

  const starPx = Math.round(size * STAR_RATIO);

  return (
    <div
      ref={containerRef}
      role="radiogroup"
      aria-label={tCommon('starRating')}
      onKeyDown={handleKeyDown}
      className={cn(
        'z-2 flex gap-x-0 focus:outline-hidden',
        {
          'cursor-default': readOnly,
        },
        className,
      )}
      {...(readOnly && { 'aria-disabled': true })}
      data-cy={dataCy}
      onMouseLeave={(e) => {
        e.stopPropagation();
        setHoverRating(null);
      }}
    >
      {[1, 2, 3, 4, 5].map((value) => (
        <button
          key={value}
          role="radio"
          aria-checked={rating === value}
          aria-label={`${value} star${value > 1 ? 's' : ''}`}
          {...(!readOnly &&
            !isLoading &&
            onChange && {
              onClick: (e) => {
                e.stopPropagation();
                setRating(rating === value ? 0 : value);
                onChange(rating === value ? 0 : value);
                setHoverRating(rating === value ? 0 : value);
              },
              onMouseEnter: (e) => {
                e.stopPropagation();
                setHoverRating(value);
              },
            })}
          disabled={readOnly || isLoading}
          {...((readOnly || isLoading) && { 'aria-disabled': true })}
          style={{ width: size, height: size }}
          className={cn(
            'flex items-center justify-center focus-within:outline-none focus-visible:bg-black/4 dark:focus-visible:bg-white/4 rounded-md',
            {
              'transition-transform duration-150 ease-in-out hover:scale-125': !readOnly || !isLoading || !onChange,
              'pointer-events-none': readOnly || isLoading || !onChange,
              'opacity-50': isLoading,
            },
          )}
          data-cy={`${dataCy}-star-${value}`}
        >
          {value <= displayRating ? (
            <Star size={starPx} className="fill-icon-base stroke-none" />
          ) : (
            <Dot size={size} className="stroke-icon-base" />
          )}
        </button>
      ))}
    </div>
  );
};
